import React, { useState, KeyboardEvent } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Send, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const MAX_MESSAGE_LENGTH = 2000; // Maximum characters per message

export function ChatInput({ onSend, isLoading = false, disabled = false }: ChatInputProps) {
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSend = () => {
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage) {
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setError(`Message is too long. Maximum ${MAX_MESSAGE_LENGTH} characters allowed.`);
      return;
    }

    if (!isLoading && !disabled) {
      setError(null);
      onSend(trimmedMessage);
      setMessage("");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessage(value);
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    
    // Show warning if approaching limit
    if (value.length > MAX_MESSAGE_LENGTH * 0.9) {
      setError(`Message is getting long (${value.length}/${MAX_MESSAGE_LENGTH} characters)`);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="p-4 border-t bg-white">
      {error && (
        <div className="mb-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
          {error}
        </div>
      )}
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <Input
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            disabled={isLoading || disabled}
            className={message.length > MAX_MESSAGE_LENGTH ? "border-red-500" : ""}
            maxLength={MAX_MESSAGE_LENGTH + 100} // Allow typing but show error
          />
          {message.length > 0 && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {message.length}/{MAX_MESSAGE_LENGTH}
            </div>
          )}
        </div>
        <Button
          onClick={handleSend}
          disabled={!message.trim() || isLoading || disabled || message.length > MAX_MESSAGE_LENGTH}
          size="icon"
          className="bg-accent hover:bg-accent/90"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

